type Config = {
  apiUrl: string;
  wsUrl: string;
  mixpanelToken: string | undefined;
  appVersion: string;
  auth0: {
    domain: string;
    clientId: string;
    redirectUri: string;
    logoutUri: string;
    audience?: string;
  };
};

// Build-time semver injected by Vite (`define` in vite.config.ts) from
// `package.json`. Exposed standalone so Sentry / Mixpanel / `window` can read
// it without depending on the rest of the config object.
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION || '0.0.0';

declare global {
  interface Window {
    __APP_VERSION__?: string;
  }
}

// Surfaces the version on `window.__APP_VERSION__` so it's inspectable from
// the browser devtools without shipping a dedicated UI surface.
export function exposeAppVersion(): void {
  if (typeof window === 'undefined') return;
  window.__APP_VERSION__ = APP_VERSION;
}

export const isDevelopment =
  import.meta.env.MODE === 'development' ||
  (typeof window !== 'undefined' &&
    !window.location.hostname.includes('deals.appchoose.io'));

export const shopifyPluginUrl = !isDevelopment
  ? `https://apps.shopify.com/choose-sync-plugin?locale=`
  : 'https://admin.shopify.com/?organization_id=129462141&no_redirect=true&redirect=/oauth/redirect_from_developer_dashboard?client_id%3D8e6bcad4bc8642d6dbc07d5ed07227af&locale=';

export const isLocalhost =
  typeof window !== 'undefined' &&
  window.location.hostname.includes('localhost');

const getAppOrigin = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  // Fallback for tools (e.g. codegen)
  return (
    import.meta.env.VITE_APP_URL ||
    (isLocalhost
      ? 'http://localhost:1993'
      : isDevelopment
        ? 'https://deals.appchoose.co'
        : 'https://deals.appchoose.io')
  );
};

const getRedirectUri = () => {
  const origin = getAppOrigin();
  return `${origin}/redirect`;
};

const getLogoutUri = () => {
  const origin = getAppOrigin();
  return origin;
};

export const getConfig = (): Config => {
  const apiUrl =
    import.meta.env.VITE_API_URL ||
    'https://deals-api-314556487628.europe-west1.run.app/graphql';
  const wsUrl =
    import.meta.env.VITE_WS_URL ||
    'wss://deals-websocket-314556487628.europe-west1.run.app/graphql';

  const domain =
    import.meta.env.VITE_AUTH0_DOMAIN || 'staging-choose.eu.auth0.com';

  const audience =
    import.meta.env.VITE_AUTH0_AUDIENCE ||
    'https://staging-choose.eu.auth0.com/api/v2/';

  // Local and dev environments use a dedicated Mixpanel project so they don't
  // pollute production analytics. Production (deals.appchoose.io) keeps its
  // own token. VITE_MIXPANEL_TOKEN still wins when explicitly set.
  const mixpanelToken =
    import.meta.env.VITE_MIXPANEL_TOKEN ||
    (isDevelopment
      ? 'a99a303a537d26e1147242d9e1c73aeb'
      : '05cc0b93700a082d24eb1c99545bd7e5');

  return {
    apiUrl,
    wsUrl,
    mixpanelToken,
    appVersion: APP_VERSION,
    auth0: {
      domain,
      clientId:
        import.meta.env.VITE_AUTH0_CLIENT_ID ||
        'uM4M8C6cJpV3wd79wliDLgNcfK7B8LuO',
      redirectUri: getRedirectUri(),
      logoutUri: getLogoutUri(),
      audience,
    },
  };
};

export const getDealsApiBaseUrl = (): string => {
  return getConfig().apiUrl.replace(/\/graphql\/?$/, '');
};

export type CsvBoxEnvironment = {
  env_name: 'dev' | 'prod';
  base_url: string;
};

export const getCsvBoxEnvironment = (): CsvBoxEnvironment => ({
  env_name: isDevelopment ? 'dev' : 'prod',
  base_url: getDealsApiBaseUrl(),
});

// --- SSE deal events (`GET /deals/:dealId/events`) ---------------------
//
// The enable/disable decision lives in `lib/deal-sse-flag.ts` (it also exposes
// the transport as a Mixpanel super property, and Mixpanel imports config — so
// keeping it there avoids a config → mixpanel import cycle). Config only owns
// the URL derivation.

/**
 * HTTP origin of the long-lived-connections service (`deals-websocket`),
 * derived from the GraphQL WebSocket URL: `wss://host/graphql` → `https://host`.
 *
 * Both Cloud Run services run the same image, so `/deals/:dealId/events` is
 * served by either — but the SSE stream must NOT go through the API service:
 * its Cloud Run request timeout (30 s) would cut the stream every 30 s, and
 * each reconnection triggers a `SYNC` (full reload of the deal and its items).
 * The websocket service is sized for connections that stay open (long
 * timeout, high concurrency, CPU never throttled), so the stream lives there.
 */
export const getDealsWebsocketBaseUrl = (): string => {
  return getConfig()
    .wsUrl.replace(/^ws(s?):\/\//, 'http$1://')
    .replace(/\/graphql\/?$/, '');
};

/**
 * Build the per-deal SSE URL. Auth is carried by the `Authorization` header
 * (the backend `$auth` plugin reads the Bearer token, same as GraphQL), so the
 * token never appears in the URL. `EventSource` can't set headers, which is
 * why the client reads the stream over `fetch` — see `useDealEventStream`.
 */
export const getDealEventsUrl = (dealId: string): string => {
  return `${getDealsWebsocketBaseUrl()}/deals/${encodeURIComponent(dealId)}/events`;
};

/**
 * Fonction utilitaire pour obtenir les paramètres d'autorisation Auth0
 * Note: redirect_uri is automatically handled by Auth0Provider
 */
export const getAuth0AuthorizationParams = () => {
  const config = getConfig();

  // Build authorization params
  const params: Record<string, string> = {
    // Standard OpenID Connect scopes
    // scope: 'openid profile email',
  };

  // Only include audience if it's defined
  if (config.auth0.audience) {
    params.audience = config.auth0.audience;
  }

  return params;
};
